/**
 * Design tokens for TypeScript consumers — the mirror of
 * `src/styles/base/colors.scss`. See docs/design/DESIGN-DIRECTION.md.
 *
 * WHY THIS FILE EXISTS
 * Most colour belongs in SCSS. But three kinds of call site cannot reach a
 * SCSS variable and cannot use a CSS custom property either:
 *
 *   1. SVG presentation attributes — `stroke="var(--x)"` does not resolve.
 *   2. Chart libraries that pass colours straight through to SVG attributes.
 *   3. Colour values computed in TS (password strength, category palettes).
 *
 * Those import from here. Anything that is plain CSS — including inline style
 * objects carrying *text* colour — should prefer the theme-aware custom
 * properties (`var(--color-text-muted)`, …) so it follows dark mode.
 *
 * KEEP IN SYNC with colors.scss. If you change a value there, change it here.
 */

export const tokens = {
  // ── Paper: the ground ─────────────────────────────────────────────────────
  paper: "#faf7f2",
  paperSunk: "#f4f1eb",
  surface: "#ffffff",
  line: "#e9e4da",
  lineStrong: "#dbd4c6",

  // ── Ink: type, nav rail, dark surfaces ────────────────────────────────────
  ink50: "#f4f6ff",
  ink100: "#eaecf5",
  ink200: "#d9dce6",
  ink300: "#bdc2d2",
  ink400: "#9da4ba",
  ink500: "#8088a3", // placeholders / disabled only — not readable text
  ink600: "#636c8b", // lightest step that may carry text
  ink700: "#4d546e",
  ink800: "#363c50",
  ink900: "#232735",
  ink950: "#141720",

  // ── Indigo: THE action colour ─────────────────────────────────────────────
  indigo50: "#f3f5ff",
  indigo100: "#e9ebff",
  indigo200: "#d6d9ff",
  indigo300: "#b9bcfb", // actions on dark surfaces
  indigo400: "#989af1",
  indigo500: "#7c7ae4",
  indigo600: "#6156d5", // primary
  indigo700: "#4c41ac", // hover / pressed
  indigo800: "#362d7f",
  indigo900: "#221c57",

  // ── Warm: the highlighter and its family ──────────────────────────────────
  mark: "#ffd11e",
  amber50: "#fff5e3",
  amber300: "#dcbe90",
  amber500: "#b27e1a",
  amber600: "#9b5e00",

  // ── Semantic. RESERVED — never brand, never decoration. ───────────────────
  // Correct/incorrect must never be expressed in a brand colour. These match
  // the values the exercise decorators carry inline; keep them in sync.
  ok: "#15803d",
  okFill: "#22a55b",
  okBg: "#f0fdf4",
  err: "#dc2626",
  errBg: "#fef2f2",
  warn: "#b45309",
  warnBg: "#fffbeb",
  info: "#3b82f6",
  infoBg: "#eff6ff",
} as const;

/**
 * Categorical series colour for charts.
 *
 * Built from the identity rather than a rainbow: the action indigo leads, the
 * highlighter follows, then progressively quieter steps. Deliberately avoids
 * green and red so a chart series can never be misread as correct/incorrect.
 */
export const chartSeries: readonly string[] = [
  tokens.indigo600,
  tokens.mark,
  tokens.indigo300,
  tokens.amber500,
  tokens.ink500,
  tokens.indigo800,
];

/** rgba() helper for the action colour, for the alpha washes in inline styles. */
export const indigoAlpha = (a: number) => `rgba(97, 86, 213, ${a})`;
